import { AutoComplete, Form, FormItemProps, Popover, Tooltip, Typography, Input, FormInstance } from 'antd';

import { useWatch } from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import clsx from 'clsx';
import { NamePath } from 'rc-field-form/lib/interface';
import React, { ChangeEvent, CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { ILoanColumn, IOption } from '../../../types/reports';
import styles from './index.module.less';
import { styleEclipses, tooltipStyle } from '../../../utils/cssUtils';
import { formatAmount } from '../../../utils/formatAmount';

interface ICustomInput {
  identifier?: NamePath;
  prefix?: string;
  suffix?: string;
  value?: string | number;
  additionalText?: {
    text: string;
    style?: CSSProperties;
  };
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  bordered?: boolean;
  disabled?: boolean;
  formItemProps?: FormItemProps;
  inputClassName?: string;
  popoverContent?: React.ReactNode;
  options?: IOption[];
  activeOption?: number;
  rowStyles?: CSSProperties;
  onFocus?: () => void;
  onBlur?: (value: string | number | readonly string[] | undefined) => void;
  placeholder?: string;
  tabIndex?: number;
  suffixContent?: string | ReactNode;
  withEclipses?: boolean;
  onChangeZero?: boolean;
  isReadOnly?: boolean;
  valueDecimalPlaces?: number;
  inputType?: 'text' | 'number';
  informationText?: string;
  allowNegative?: boolean;
}

const CustomInput = ({
  identifier,
  prefix,
  suffix,
  value,
  additionalText,
  onChange,
  onSelect,
  bordered,
  disabled,
  formItemProps,
  inputClassName,
  options,
  popoverContent,
  rowStyles,
  onFocus,
  onBlur,
  placeholder,
  tabIndex,
  suffixContent,
  withEclipses,
  onChangeZero,
  isReadOnly = false,
  valueDecimalPlaces = 0,
  inputType = 'number',
  informationText,
  allowNegative = false,
}: ICustomInput) => {
  const form = useFormInstance();
  const formItemValue = useWatch(identifier || formItemProps?.name || '0', form);
  const [inputValue, setInputValue] = useState<string | number>();
  const [isPopoverOpened, setIsPopoverOpened] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const isPrefixDollarSign = prefix?.includes('$');

  useEffect(() => {
    let preparedValue;

    if (formItemValue === "undefined" || formItemValue === null) {
      preparedValue = onChangeZero ? undefined : '0';
    } else {
      preparedValue = formItemValue;
    }

    // setInputValue(isPrefixDollarSign && preparedValue ? formatAmount(preparedValue,
    //   { max: valueDecimalPlaces }) : preparedValue);

    setInputValue(preparedValue);
  }, [formItemValue]);

  useEffect(() => {
    if (!value || !identifier) return;

    form.setFieldValue(identifier, value);
  }, [value]);

  const handleChange = (newValue: string) => {
    if (!identifier) return;

    form.setFieldValue(identifier, parseFloat(newValue || '0'));
    onChange?.(newValue);
    // handleLoansUpdate?.();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let cleanedValue = event.target?.value;

    if (prefix) {
      cleanedValue = cleanedValue.replace(prefix, '')
    }

    if (suffix) {
      cleanedValue = cleanedValue.replace(suffix, '')
    }

    const regex = allowNegative ? /[^0-9.-]/g : /[^0-9.]/g;

    handleChange(cleanedValue.replace(regex, ''));
  }

  const styleText = withEclipses ? { ...rowStyles, ...styleEclipses } : { ...rowStyles };

  // Allow decimals only for percentage fields
  // const handleIsAllowed = (values: NumberFormatValues) =>
  //   !isPrefixDollarSign || !values?.value.includes('.');

  if (popoverContent) {
    return (
      <Popover
        ref={ref}
        showArrow={false}
        trigger="click"
        open={isPopoverOpened}
        onOpenChange={(visible) => setIsPopoverOpened(visible)}
        placement="bottomLeft"
        overlayClassName="popover-select"
        content={
          <div
            onBlur={() => {
              setIsPopoverOpened(false);
            }}
          >
            {popoverContent}
          </div>
        }
      >
        <Tooltip title={suffixContent} trigger="hover" placement="top" overlayStyle={tooltipStyle}>
          <div className={inputClassName !== 'bg-white' ? styles.inputWrapper : ''}>
            <Form.Item
              {...formItemProps}
              className={clsx('custom-input-form-item', 'label-gray', formItemProps?.className, {
                [styles.bordered]: bordered,
              })}
              style={rowStyles}
            >
              <NumericFormat
                style={styleText}
                thousandSeparator
                disabled={disabled}
                readOnly={isReadOnly}
                onChange={handleInputChange}
                // isAllowed={handleIsAllowed}
                className={inputClassName}
                onFocus={() => {
                  onFocus?.();
                  setIsPopoverOpened(true);
                }}
                onBlur={() => onBlur?.(inputValue)}
                placeholder={placeholder}
                value={inputValue}
                prefix={prefix}
                suffix={suffix}
                tabIndex={tabIndex}
              />
            </Form.Item>
          </div>
        </Tooltip>
      </Popover>
    );
  }

  if (options) {
    return (
      <Tooltip title={suffixContent} trigger="hover" placement="top" overlayStyle={tooltipStyle}>
        <div className={inputClassName !== 'bg-white' ? styles.inputWrapper : ''}>
          <Form.Item
            {...formItemProps}
            className={clsx('custom-input-form-item flex-row gap-4', 'label-gray', formItemProps?.className, {
              [styles.bordered]: bordered,
            })}
            style={rowStyles}
          >
            <AutoComplete
              className="autocomplete-borderless"
              dropdownMatchSelectWidth={false}
              options={options.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              onSelect={(selectValue) => {
                if (inputType === 'number') {
                  handleChange(selectValue);
                  onSelect?.(selectValue);
                } else {
                  onChange?.(selectValue);
                }
              }}
              tabIndex={tabIndex}
              disabled={disabled}
              getPopupContainer={() => document.body}
            >
              {inputType === 'number' && (
                <NumericFormat
                  style={styleText}
                  thousandSeparator
                  disabled={disabled}
                  readOnly={isReadOnly}
                  onChange={handleInputChange}
                  // isAllowed={handleIsAllowed}
                  className={inputClassName}
                  onFocus={() => onFocus?.()}
                  onBlur={() => onBlur?.(inputValue)}
                  placeholder={placeholder}
                  value={inputValue}
                  prefix={prefix}
                  suffix={suffix}
                />
              )}

              {inputType === 'text' && (
                <Input
                  type="text"
                  style={styleText}
                  disabled={disabled}
                  readOnly={isReadOnly}
                  className={inputClassName}
                  onBlur={(event) => {
                    onBlur?.(event?.target?.value || "")
                  }}
                  placeholder={placeholder}
                  prefix={prefix}
                  suffix={suffix}
                />
              )}
            </AutoComplete>
          </Form.Item>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={suffixContent} trigger="hover"
      placement={identifier?.[2] === 'purchasePrice' ? 'topLeft' : 'top'} overlayStyle={tooltipStyle}>
      <div className={inputClassName !== 'bg-white' ? styles.inputWrapper : ''}>
        <Form.Item
          {...formItemProps}
          className={clsx('custom-input-form-item', 'label-gray', formItemProps?.className, {
            [styles.bordered]: bordered,
          })}
        >
          <NumericFormat
            style={styleText}
            thousandSeparator
            disabled={disabled}
            readOnly={isReadOnly}
            displayType={additionalText ? 'text' : 'input'}
            onChange={handleInputChange}
            // isAllowed={handleIsAllowed}
            className={inputClassName}
            onFocus={() => onFocus?.()}
            onBlur={() => onBlur?.(inputValue)}
            placeholder={placeholder}
            value={additionalText && !inputValue ? 0 : inputValue}
            prefix={prefix}
            suffix={suffix}
            tabIndex={tabIndex}
          />
          {informationText && (
            <span style={rowStyles}>
              {informationText}
            </span>
          )}
          {additionalText && (
            <Typography.Text className={clsx(styles.label)} style={additionalText.style}>
              {additionalText.text}
            </Typography.Text>
          )}
        </Form.Item>
      </div>
    </Tooltip>
  );
};

CustomInput.defaultProps = {
  identifier: undefined,
  prefix: undefined,
  suffix: undefined,
  value: undefined,
  additionalText: undefined,
  onChange: undefined,
  onSelect: undefined,
  bordered: false,
  disabled: false,
  formItemProps: undefined,
  inputClassName: undefined,
  showArrow: false,
  popoverContent: undefined,
  options: undefined,
  activeOption: undefined,
  onOptionClick: undefined,
  rowStyles: undefined,
  onBlur: undefined,
  onFocus: undefined,
  placeholder: undefined,
  tabIndex: undefined,
  suffixContent: undefined,
  withEclipses: false,
  onChangeZero: false,
};

export default CustomInput;
